import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAziende, createAzienda, updateAzienda, toggleAzienda, deleteAzienda, aggiungiLinks, sendAziendaWelcomeEmail, getAziendaQRRanges, rimuoviQRDaAzienda, changeAziendaPassword } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Building2, Mail, ExternalLink, Pencil, Trash2, Loader2, QrCode, Send, X, KeyRound } from 'lucide-react';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';

const AziendePage = () => {
  const [aziende, setAziende] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedAziendaForQR, setSelectedAziendaForQR] = useState(null);
  const [aziendaQRData, setAziendaQRData] = useState(null);
  const [loadingQRData, setLoadingQRData] = useState(false);
  const [removingQR, setRemovingQR] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aziendaToDelete, setAziendaToDelete] = useState(null);
  const [qrRange, setQrRange] = useState({ inizio: '', fine: '' });
  const [addingQR, setAddingQR] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogAzienda, setEmailDialogAzienda] = useState(null);
  const [emailPassword, setEmailPassword] = useState('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordDialogAzienda, setPasswordDialogAzienda] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [notifyByEmail, setNotifyByEmail] = useState(true);
  const [changingPassword, setChangingPassword] = useState(false);
  const [formData, setFormData] = useState({
    ragione_sociale: '',
    email: '',
    password: '',
    indirizzo: '',
    telefono: '',
    sito_web: '',
    pec: '',
    sdi: '',
    tipo_account: 'base',
    qr_start: '',
    qr_end: '',
  });

  useEffect(() => {
    loadAziende();
  }, []);

  const loadAziende = async () => {
    try {
      const response = await getAziende();
      setAziende(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento aziende');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepara i dati per l'invio
      const submitData = { ...formData };
      // Converti qr_start e qr_end in numeri se presenti (solo per creazione)
      if (!editMode) {
        if (submitData.qr_start) submitData.qr_start = parseInt(submitData.qr_start);
        else delete submitData.qr_start;
        if (submitData.qr_end) submitData.qr_end = parseInt(submitData.qr_end);
        else delete submitData.qr_end;
      } else {
        // In edit mode non inviare i campi QR
        delete submitData.qr_start;
        delete submitData.qr_end;
      }
      
      if (editMode && editingId) {
        await updateAzienda(editingId, submitData);
        toast.success('Azienda aggiornata');
      } else {
        const qrCount = (submitData.qr_start && submitData.qr_end) 
          ? (submitData.qr_end - submitData.qr_start + 1) 
          : 10;
        await createAzienda(submitData);
        toast.success(`Azienda creata con ${qrCount} QR Code`);
      }
      setDialogOpen(false);
      resetForm();
      loadAziende();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const resetForm = () => {
    setFormData({ ragione_sociale: '', email: '', password: '', indirizzo: '', telefono: '', sito_web: '', pec: '', sdi: '', tipo_account: 'base', qr_start: '', qr_end: '' });
    setEditMode(false);
    setEditingId(null);
  };

  const handleEdit = (azienda) => {
    setFormData({
      ragione_sociale: azienda.ragione_sociale || '',
      email: azienda.email || '',
      password: '', // Password non viene mostrata in modifica
      indirizzo: azienda.indirizzo || '',
      telefono: azienda.telefono || '',
      sito_web: azienda.sito_web || '',
      pec: azienda.pec || '',
      sdi: azienda.sdi || '',
      tipo_account: azienda.tipo_account || 'base',
    });
    setEditingId(azienda.id_azienda);
    setEditMode(true);
    setDialogOpen(true);
  };

  const openDeleteDialog = (azienda) => {
    setAziendaToDelete(azienda);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!aziendaToDelete) return;
    setDeleting(aziendaToDelete.id_azienda);
    try {
      await deleteAzienda(aziendaToDelete.id_azienda);
      toast.success('Azienda eliminata');
      loadAziende();
      setDeleteDialogOpen(false);
      setAziendaToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleAzienda(id);
      loadAziende();
      toast.success('Stato aggiornato');
    } catch (error) {
      toast.error('Errore');
    }
  };

  const openQRDialog = async (azienda) => {
    setSelectedAziendaForQR(azienda);
    setQrRange({ inizio: '', fine: '' });
    setAziendaQRData(null);
    setQrDialogOpen(true);
    
    // Carica i range QR esistenti
    setLoadingQRData(true);
    try {
      const response = await getAziendaQRRanges(azienda.id_azienda);
      setAziendaQRData(response.data);
    } catch (error) {
      console.error('Errore caricamento QR ranges:', error);
    } finally {
      setLoadingQRData(false);
    }
  };

  const openEmailDialog = (azienda) => {
    setEmailDialogAzienda(azienda);
    setEmailPassword('');
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailDialogAzienda) return;
    setSendingEmail(emailDialogAzienda.id_azienda);
    try {
      await sendAziendaWelcomeEmail(emailDialogAzienda.id_azienda, emailPassword || null);
      toast.success(`Email inviata a ${emailDialogAzienda.email}`);
      setEmailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email');
    } finally {
      setSendingEmail(null);
    }
  };

  const openPasswordDialog = (azienda) => {
    setPasswordDialogAzienda(azienda);
    setNewPassword('');
    setConfirmPassword('');
    setNotifyByEmail(true);
    setPasswordDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!passwordDialogAzienda) return;
    if (!newPassword || newPassword.length < 6) {
      toast.error('La password deve contenere almeno 6 caratteri');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    setChangingPassword(true);
    try {
      const response = await changeAziendaPassword(passwordDialogAzienda.id_azienda, newPassword, notifyByEmail);
      if (notifyByEmail && response.data?.email_sent === false) {
        toast.warning('Password aggiornata ma invio email fallito');
      } else {
        toast.success(notifyByEmail 
          ? `Password aggiornata e notificata via email a ${passwordDialogAzienda.email}`
          : 'Password aggiornata con successo'
        );
      }
      setPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore modifica password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAddQR = async () => {
    if (!qrRange.inizio || !qrRange.fine) {
      toast.error('Inserisci il range di QR Code');
      return;
    }
    const inizio = parseInt(qrRange.inizio);
    const fine = parseInt(qrRange.fine);
    if (isNaN(inizio) || isNaN(fine) || fine < inizio) {
      toast.error('Range non valido');
      return;
    }
    setAddingQR(true);
    try {
      const response = await aggiungiLinks(selectedAziendaForQR.id_azienda, inizio, fine);
      const data = response.data;
      if (data.skipped && data.skipped.length > 0) {
        toast.warning(`Aggiunti ${data.links_aggiunti} QR. Saltati: ${data.skipped.join(', ')} (già esistenti)`);
      } else {
        toast.success(`Aggiunti ${data.links_aggiunti} QR Code (${inizio}-${fine})`);
      }
      // Ricarica i dati QR aggiornati
      setQrRange({ inizio: '', fine: '' });
      const qrResponse = await getAziendaQRRanges(selectedAziendaForQR.id_azienda);
      setAziendaQRData(qrResponse.data);
      loadAziende();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiunta QR');
    } finally {
      setAddingQR(false);
    }
  };

  const handleRemoveQR = async (range) => {
    if (!window.confirm(`Sei sicuro di voler rimuovere il range QR ${range.start}-${range.end}? I QR attivi o collegati a elogi non verranno rimossi.`)) {
      return;
    }
    
    const rangeKey = `${range.start}-${range.end}`;
    setRemovingQR(rangeKey);
    try {
      const response = await rimuoviQRDaAzienda(selectedAziendaForQR.id_azienda, range.start, range.end);
      toast.success(`Rimossi ${response.data.qr_rimossi} QR Code`);
      // Ricarica i dati QR aggiornati
      const qrResponse = await getAziendaQRRanges(selectedAziendaForQR.id_azienda);
      setAziendaQRData(qrResponse.data);
      loadAziende();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore rimozione QR');
    } finally {
      setRemovingQR(null);
    }
  };

  const filteredAziende = aziende.filter(a => 
    a.ragione_sociale?.toLowerCase().includes(search.toLowerCase()) ||
    a.email?.toLowerCase().includes(search.toLowerCase()) ||
    a.id_azienda?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="aziende-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 
            className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Aziende
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Gestione pompe funebri partner</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="new-azienda-btn" onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Nuova Azienda
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                {editMode ? 'Modifica Azienda' : 'Nuova Azienda'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="ragione_sociale">Ragione Sociale *</Label>
                  <Input
                    id="ragione_sociale"
                    value={formData.ragione_sociale}
                    onChange={(e) => setFormData({...formData, ragione_sociale: e.target.value})}
                    required
                    data-testid="input-ragione-sociale"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="input-email"
                  />
                </div>
                {!editMode && (
                  <div className="sm:col-span-2">
                    <Label htmlFor="password">Password Accesso *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                      placeholder="Password per l'accesso dell'azienda"
                      data-testid="input-password"
                    />
                  </div>
                )}
                <div className="sm:col-span-2">
                  <Label htmlFor="indirizzo">Indirizzo</Label>
                  <Input
                    id="indirizzo"
                    value={formData.indirizzo}
                    onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="telefono">Telefono</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="sito_web">Sito Web</Label>
                  <Input
                    id="sito_web"
                    value={formData.sito_web}
                    onChange={(e) => setFormData({...formData, sito_web: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="pec">PEC</Label>
                  <Input
                    id="pec"
                    value={formData.pec}
                    onChange={(e) => setFormData({...formData, pec: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="sdi">Codice SDI</Label>
                  <Input
                    id="sdi"
                    value={formData.sdi}
                    onChange={(e) => setFormData({...formData, sdi: e.target.value})}
                  />
                </div>
                {/* Range QR Code - solo in creazione */}
                {!editMode && (
                  <>
                    <div className="sm:col-span-2 pt-2">
                      <div className="text-sm font-medium text-slate-700 mb-2">Range QR Code da assegnare</div>
                      <p className="text-xs text-slate-500 mb-2">Specifica l'ID iniziale e finale dei QR code. Lascia vuoto per assegnare automaticamente 10 QR.</p>
                    </div>
                    <div>
                      <Label htmlFor="qr_start">ID QR Iniziale</Label>
                      <Input
                        id="qr_start"
                        type="number"
                        min="1"
                        placeholder="es. 1"
                        value={formData.qr_start}
                        onChange={(e) => setFormData({...formData, qr_start: e.target.value})}
                        data-testid="input-qr-start"
                      />
                    </div>
                    <div>
                      <Label htmlFor="qr_end">ID QR Finale</Label>
                      <Input
                        id="qr_end"
                        type="number"
                        min="1"
                        placeholder="es. 10"
                        value={formData.qr_end}
                        onChange={(e) => setFormData({...formData, qr_end: e.target.value})}
                        data-testid="input-qr-end"
                      />
                    </div>
                  </>
                )}
                <div className="sm:col-span-2">
                  <Label htmlFor="tipo_account">Tipo Account</Label>
                  <Select 
                    value={formData.tipo_account} 
                    onValueChange={(val) => setFormData({...formData, tipo_account: val})}
                  >
                    <SelectTrigger data-testid="select-tipo-account">
                      <SelectValue placeholder="Seleziona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="base">Base (€150 per attivazione)</SelectItem>
                      <SelectItem value="premium">Premium (€100 per attivazione)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="w-full sm:w-auto">
                  Annulla
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="submit-azienda-btn">
                  {editMode ? 'Salva Modifiche' : 'Crea Azienda'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cerca per nome, email o ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-aziende"
          />
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {filteredAziende.map((azienda) => (
          <Card key={azienda.id_azienda} className={!azienda.attivo ? 'opacity-60' : ''} data-testid={`azienda-card-${azienda.id_azienda}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100">
                    <Building2 className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{azienda.ragione_sociale}</p>
                    <p className="text-xs text-slate-500">{azienda.id_azienda}</p>
                  </div>
                </div>
                <button onClick={() => handleToggle(azienda.id_azienda)}>
                  {azienda.attivo ? (
                    <Badge className="bg-green-100 text-green-700">Attiva</Badge>
                  ) : (
                    <Badge variant="secondary">Disattiva</Badge>
                  )}
                </button>
              </div>

              <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                <Mail className="h-4 w-4" />
                <span className="truncate">{azienda.email}</span>
              </div>

              <div className="flex items-center gap-4 text-sm mb-4">
                <div className="flex items-center gap-1">
                  <QrCode className="h-4 w-4 text-slate-400" />
                  <span className="font-medium">{azienda.qr_totali}</span>
                  <span className="text-slate-500">totali</span>
                </div>
                <div className="text-green-600">
                  <span className="font-medium">{azienda.qr_attivi}</span>
                  <span className="text-slate-500 ml-1">attivi</span>
                </div>
                {azienda.qr_totali - azienda.qr_attivi > 0 && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    {azienda.qr_totali - azienda.qr_attivi} disp.
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => handleEdit(azienda)} className="flex-1">
                  <Pencil className="h-4 w-4 mr-1" />
                  Modifica
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => openEmailDialog(azienda)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  disabled={sendingEmail === azienda.id_azienda}
                >
                  {sendingEmail === azienda.id_azienda ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Email
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => openPasswordDialog(azienda)}
                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  data-testid={`change-password-mobile-${azienda.id_azienda}`}
                >
                  <KeyRound className="h-4 w-4 mr-1" />
                  Password
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => openQRDialog(azienda)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <QrCode className="h-4 w-4 mr-1" />
                  +QR
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => openDeleteDialog(azienda)}
                  disabled={deleting === azienda.id_azienda}
                >
                  {deleting === azienda.id_azienda ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
                <Link to={`/aziende/${azienda.id_azienda}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Gestisci
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ))}
        {filteredAziende.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            Nessuna azienda trovata
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="aziende-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Ragione Sociale</th>
                  <th className="text-left p-4 font-medium text-slate-600">Email</th>
                  <th className="text-center p-4 font-medium text-slate-600">QR Totali</th>
                  <th className="text-center p-4 font-medium text-slate-600">QR Attivi</th>
                  <th className="text-center p-4 font-medium text-slate-600">Disponibili</th>
                  <th className="text-center p-4 font-medium text-slate-600">Stato</th>
                  <th className="text-right p-4 font-medium text-slate-600">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredAziende.map((azienda) => (
                  <tr 
                    key={azienda.id_azienda} 
                    className={`border-b hover:bg-slate-50 transition-colors ${!azienda.attivo ? 'opacity-50' : ''}`}
                    data-testid={`azienda-row-${azienda.id_azienda}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-slate-100">
                          <Building2 className="h-4 w-4 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{azienda.ragione_sociale}</p>
                          <p className="text-xs text-slate-500">{azienda.id_azienda}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="h-4 w-4" />
                        {azienda.email}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span className="font-semibold">{azienda.qr_totali}</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="font-semibold text-green-600">{azienda.qr_attivi}</span>
                    </td>
                    <td className="p-4 text-center">
                      {azienda.qr_totali - azienda.qr_attivi > 0 ? (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          {azienda.qr_totali - azienda.qr_attivi}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <button onClick={() => handleToggle(azienda.id_azienda)}>
                        {azienda.attivo ? (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-200">Attiva</Badge>
                        ) : (
                          <Badge variant="secondary">Disattiva</Badge>
                        )}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEdit(azienda)}
                          data-testid={`edit-${azienda.id_azienda}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEmailDialog(azienda)}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          disabled={sendingEmail === azienda.id_azienda}
                          data-testid={`email-${azienda.id_azienda}`}
                        >
                          {sendingEmail === azienda.id_azienda ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openPasswordDialog(azienda)}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          data-testid={`change-password-${azienda.id_azienda}`}
                          title="Modifica password"
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openQRDialog(azienda)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`add-qr-${azienda.id_azienda}`}
                        >
                          <QrCode className="h-4 w-4 mr-1" />
                          +QR
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openDeleteDialog(azienda)}
                          disabled={deleting === azienda.id_azienda}
                          data-testid={`delete-${azienda.id_azienda}`}
                        >
                          {deleting === azienda.id_azienda ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                        <Link to={`/aziende/${azienda.id_azienda}`}>
                          <Button variant="outline" size="sm" data-testid={`gestisci-${azienda.id_azienda}`}>
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Gestisci
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredAziende.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              Nessuna azienda trovata
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog per aggiungere QR */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Gestione QR Code
            </DialogTitle>
          </DialogHeader>
          {selectedAziendaForQR && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedAziendaForQR.ragione_sociale}</p>
                <p className="text-sm text-slate-500">{selectedAziendaForQR.id_azienda}</p>
              </div>
              
              {/* Range QR Assegnati */}
              <div className="border rounded-lg p-3">
                <p className="font-medium text-sm mb-2">QR Code Assegnati</p>
                {loadingQRData ? (
                  <div className="flex items-center gap-2 text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Caricamento...</span>
                  </div>
                ) : aziendaQRData ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {aziendaQRData.ranges.length > 0 ? (
                        aziendaQRData.ranges.map((range, idx) => {
                          const rangeKey = `${range.start}-${range.end}`;
                          const isRemoving = removingQR === rangeKey;
                          return (
                            <Badge 
                              key={idx} 
                              variant="secondary" 
                              className="text-xs flex items-center gap-1 pr-1"
                            >
                              <span>{range.start === range.end ? range.start : `${range.start}-${range.end}`}</span>
                              <button
                                onClick={() => handleRemoveQR(range)}
                                disabled={isRemoving}
                                className="ml-1 hover:bg-red-100 rounded p-0.5 text-slate-500 hover:text-red-600 transition-colors"
                                title="Rimuovi range"
                              >
                                {isRemoving ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <X className="h-3 w-3" />
                                )}
                              </button>
                            </Badge>
                          );
                        })
                      ) : (
                        <span className="text-sm text-slate-500">Nessun QR assegnato</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-2 grid grid-cols-3 gap-2">
                      <span>Totali: <strong>{aziendaQRData.totale}</strong></span>
                      <span>Attivi: <strong>{aziendaQRData.attivi}</strong></span>
                      <span>Liberi: <strong>{aziendaQRData.disponibili}</strong></span>
                    </div>
                    {aziendaQRData.attivi > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ I QR attivi o collegati a elogi non possono essere rimossi
                      </p>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-slate-500">Dati non disponibili</span>
                )}
              </div>
              
              {/* Aggiungi nuovi QR */}
              <div className="border-t pt-4">
                <p className="font-medium text-sm mb-3">Aggiungi Nuovi QR Code</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="range_inizio">Da (numero)</Label>
                    <Input
                      id="range_inizio"
                      type="number"
                      placeholder="Es: 101"
                      value={qrRange.inizio}
                      onChange={(e) => setQrRange({...qrRange, inizio: e.target.value})}
                      data-testid="input-qr-inizio"
                    />
                  </div>
                  <div>
                    <Label htmlFor="range_fine">A (numero)</Label>
                    <Input
                      id="range_fine"
                      type="number"
                      placeholder="Es: 110"
                      value={qrRange.fine}
                      onChange={(e) => setQrRange({...qrRange, fine: e.target.value})}
                      data-testid="input-qr-fine"
                    />
                  </div>
                </div>
                {qrRange.inizio && qrRange.fine && parseInt(qrRange.fine) >= parseInt(qrRange.inizio) && (
                  <p className="text-sm text-slate-500 mt-2">
                    Verranno aggiunti <strong>{parseInt(qrRange.fine) - parseInt(qrRange.inizio) + 1}</strong> QR Code
                  </p>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setQrDialogOpen(false)}>
                  Chiudi
                </Button>
                <Button 
                  onClick={handleAddQR} 
                  disabled={addingQR || !qrRange.inizio || !qrRange.fine}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="submit-qr-btn"
                >
                  {addingQR ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <QrCode className="h-4 w-4 mr-2" />}
                  Aggiungi QR
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Invio Email con Password */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Invia Email di Benvenuto
            </DialogTitle>
          </DialogHeader>
          {emailDialogAzienda && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{emailDialogAzienda.ragione_sociale}</p>
                <p className="text-sm text-slate-500">{emailDialogAzienda.email}</p>
              </div>
              <div>
                <Label htmlFor="email_password">Password da includere nell'email</Label>
                <Input
                  id="email_password"
                  type="text"
                  placeholder="Inserisci la password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">
                  La password verrà mostrata nell'email. Lascia vuoto se non vuoi includerla.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Invia Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Password */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Modifica Password Azienda
            </DialogTitle>
          </DialogHeader>
          {passwordDialogAzienda && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{passwordDialogAzienda.ragione_sociale}</p>
                <p className="text-sm text-slate-500">{passwordDialogAzienda.email}</p>
              </div>
              <div>
                <Label htmlFor="new_password">Nuova Password *</Label>
                <Input
                  id="new_password"
                  type="password"
                  placeholder="Minimo 6 caratteri"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="input-new-password"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <Label htmlFor="confirm_password">Conferma Password *</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  placeholder="Ripeti la password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="input-confirm-password"
                  autoComplete="new-password"
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox 
                  id="notify_by_email"
                  checked={notifyByEmail}
                  onCheckedChange={setNotifyByEmail}
                  data-testid="checkbox-notify-email"
                />
                <Label htmlFor="notify_by_email" className="text-sm font-normal cursor-pointer">
                  Notifica l'azienda via email con la nuova password
                </Label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setPasswordDialogOpen(false)} disabled={changingPassword}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleChangePassword}
                  disabled={changingPassword || !newPassword || !confirmPassword}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="submit-change-password-btn"
                >
                  {changingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                  Aggiorna Password
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Elimina Azienda"
        description="Eliminando questa azienda, tutti gli elogi associati verranno riassegnati all'amministratore. Questa azione è irreversibile."
        itemName={aziendaToDelete?.ragione_sociale}
        loading={deleting === aziendaToDelete?.id_azienda}
      />
    </div>
  );
};

export default AziendePage;
